import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface RevenueBreakdown {
  total_cart_value: number;
  total_discount_value: number;
  total_delivery_fees_paid_by_customer: number;
  total_delivery_fees_paid_by_restaurant: number;
  total_stripe_fees: number;
  total_commission_fees: number;
  total_cogs: number;
  total_free_item_costs: number;
  gross_revenue: number;
  net_revenue: number;
  net_profit: number;
  sms_cost: number;
  orders_count: number;
  cogs_percentage: number;
  promo_discount_type: string;
  promo_item_cost: number;
  promo_item_menu_price: number;
}

export function useCampaignRevenueBreakdown(campaignId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-revenue-breakdown', campaignId],
    queryFn: async (): Promise<RevenueBreakdown | null> => {
      if (!campaignId) return null;

      // Get campaign details and promo code
      const { data: campaign, error: campaignError } = await supabase
        .from('sms_campaigns')
        .select(`
          id,
          schedule_time,
          attribution_window_days,
          actual_cost,
          total_estimated_units,
          cost_per_segment,
          promo_code_id,
          promo_codes!sms_campaigns_promo_code_id_fkey (
            code,
            discount_type,
            item_cost,
            item_menu_price
          ),
          restaurants_master (
            average_cogs_percentage
          )
        `)
        .eq('id', campaignId)
        .single();

      if (campaignError || !campaign) {
        throw new Error('Campaign not found');
      }

      const promoCode = campaign.promo_codes?.code;
      if (!promoCode) {
        throw new Error('No promo code associated with campaign');
      }

      // Calculate attribution window
      const startDate = campaign.schedule_time;
      let endDate: string;
      
      if (campaign.attribution_window_days) {
        const end = new Date(startDate);
        end.setDate(end.getDate() + campaign.attribution_window_days);
        endDate = end.toISOString();
      } else {
        endDate = new Date().toISOString();
      }

      // Get detailed order breakdown for this promo code within attribution window
      const { data: orders, error: ordersError } = await supabase
        .from('orders')
        .select(`
          cart_value,
          discount_value,
          delivery_fee_on_pumpd,
          uberdirect_fee,
          pumpd_stripe_fee,
          pumpd_commission,
          pumpd_total_cost_for_customer,
          pumpd_net_income
        `)
        .eq('promo_code', promoCode)
        .gte('date_and_time', startDate)
        .lte('date_and_time', endDate);

      if (ordersError) {
        throw new Error('Failed to fetch order details');
      }

      if (!orders || orders.length === 0) {
        return {
          total_cart_value: 0,
          total_discount_value: 0,
          total_delivery_fees_paid_by_customer: 0,
          total_delivery_fees_paid_by_restaurant: 0,
          total_stripe_fees: 0,
          total_commission_fees: 0,
          total_cogs: 0,
          total_free_item_costs: 0,
          gross_revenue: 0,
          net_revenue: 0,
          net_profit: 0,
          sms_cost: 0,
          orders_count: 0,
          cogs_percentage: campaign.restaurants_master?.average_cogs_percentage || 30,
          promo_discount_type: campaign.promo_codes?.discount_type || 'percentage',
          promo_item_cost: campaign.promo_codes?.item_cost || 0,
          promo_item_menu_price: campaign.promo_codes?.item_menu_price || 0
        };
      }

      // Calculate totals
      const totalCartValue = orders.reduce((sum, order) => sum + (order.cart_value || 0), 0);
      
      // Handle discount value based on promo type
      const promoDiscountType = campaign.promo_codes?.discount_type || 'percentage';
      const itemCost = campaign.promo_codes?.item_cost || 0;
      const itemMenuPrice = campaign.promo_codes?.item_menu_price || 0;
      
      let totalDiscountValue;
      let totalFreeItemCosts = 0;
      
      if (promoDiscountType === 'free_item') {
        // For free items, discount value represents the value given to customers
        totalDiscountValue = orders.length * itemMenuPrice;
        // Track the cost of free items for net profit calculation
        totalFreeItemCosts = orders.length * itemCost;
      } else {
        // For regular discounts, use actual discount_value from orders
        totalDiscountValue = orders.reduce((sum, order) => sum + (order.discount_value || 0), 0);
      }
      
      const totalDeliveryFeesByCustomer = orders.reduce((sum, order) => sum + (order.delivery_fee_on_pumpd || 0), 0);
      const totalDeliveryFeesByRestaurant = orders.reduce((sum, order) => sum + (order.uberdirect_fee || 0), 0);
      const totalStripeFees = orders.reduce((sum, order) => sum + (order.pumpd_stripe_fee || 0), 0);
      const totalCommissionFees = orders.reduce((sum, order) => sum + (order.pumpd_commission || 0), 0);
      const grossRevenue = orders.reduce((sum, order) => sum + (order.pumpd_total_cost_for_customer || 0), 0);
      const restaurantNetIncome = orders.reduce((sum, order) => sum + (order.pumpd_net_income || 0), 0);
      
      const smsActualCost = campaign.actual_cost !== null ? campaign.actual_cost : 
        (campaign.total_estimated_units * (campaign.cost_per_segment || 0.15));
      
      const netRevenue = restaurantNetIncome - smsActualCost;
      
      const cogsPercentage = campaign.restaurants_master?.average_cogs_percentage || 30;
      const totalCogs = totalCartValue * (cogsPercentage / 100);
      // Net profit includes free item costs
      const netProfit = netRevenue - totalCogs - totalFreeItemCosts;

      return {
        total_cart_value: totalCartValue,
        total_discount_value: totalDiscountValue,
        total_delivery_fees_paid_by_customer: totalDeliveryFeesByCustomer,
        total_delivery_fees_paid_by_restaurant: totalDeliveryFeesByRestaurant,
        total_stripe_fees: totalStripeFees,
        total_commission_fees: totalCommissionFees,
        total_cogs: totalCogs,
        total_free_item_costs: totalFreeItemCosts,
        gross_revenue: grossRevenue,
        net_revenue: netRevenue,
        net_profit: netProfit,
        sms_cost: smsActualCost,
        orders_count: orders.length,
        cogs_percentage: cogsPercentage,
        promo_discount_type: promoDiscountType,
        promo_item_cost: itemCost,
        promo_item_menu_price: itemMenuPrice
      };
    },
    enabled: !!campaignId,
    staleTime: 300000, // 5 minutes
  });
}